import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/app.materials';
import { FormsModule } from '@angular/forms';
import { PortfolioService } from '../../pages/portfolio/portfolio.service';

export enum ColumnType {
  Simple = 1,
  Percentage,
  Number2Decimal,
  Input,
  CompanyInfo,
  NumberMin1Decimal,
}

export interface CimDatatableColumn {
  id: string;
  label: string;
  type: ColumnType | number;
  bold?: boolean;
  canHide?: boolean;
  textRight?: boolean;
  suffix?: string;
  inputDisabled?: boolean;
}

@Component({
  selector: 'app-cim-datatable',
  templateUrl: './cim-datatable.component.html',
  styleUrls: ['./cim-datatable.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule],
})
export class CimDatatableComponent implements OnInit, OnChanges {

  @Input() columns: CimDatatableColumn[] = [];
  @Input() data: any[] = [];
  @Input() footer: any = null;
  @Input() inputDisabled = false;
  @Input() needPaginator = false;
  @Input() pageSize = 20;

  @Input() sortProp: string = null;
  @Input() sortDirection: 'asc' | 'desc' | '' = '';
  @Input() sortDisabled = false;
  @Input() disableRightPadding = false;

  @Output() inputChange = new EventEmitter<any>();

  sortKey: string | null = null;
  sortDir: 'asc' | 'desc' | null = null;
  pageIndex = 0;

  readonly ColumnType = ColumnType;

  constructor(private service: PortfolioService) {}

  ngOnInit(): void {
    if (this.sortProp) {
      this.sortKey = this.sortProp;
      this.sortDir = this.sortDirection === 'desc' ? 'desc' : 'asc';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.sortProp || changes.sortDirection) {
      if (this.sortProp) {
        this.sortKey = this.sortProp;
        this.sortDir = this.sortDirection === 'desc' ? 'desc' : 'asc';
      }
    }
    if (changes.data && this.pageIndex > 0) {
      const totalPages = Math.max(1, Math.ceil((this.data?.length ?? 0) / this.pageSize));
      if (this.pageIndex >= totalPages) {
        this.pageIndex = totalPages - 1;
      }
    }
  }

  get visibleColumns(): CimDatatableColumn[] {
    return this.columns.filter((c) => !(this.inputDisabled && c.canHide));
  }

  get sortedData(): any[] {
    if (!this.sortKey || this.sortDir === null) return this.data ?? [];
    const key = this.sortKey;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    const rows = (this.data ?? []).slice();
    rows.sort((a, b) => {
      const va = a?.[key];
      const vb = b?.[key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb)) * dir;
    });
    return rows;
  }

  get displayRows(): any[] {
    if (!this.needPaginator) return this.sortedData;
    const start = this.pageIndex * this.pageSize;
    return this.sortedData.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.sortedData?.length ?? 0) / this.pageSize));
  }

  prevPage(): void {
    if (this.pageIndex > 0) this.pageIndex -= 1;
  }

  nextPage(): void {
    if (this.pageIndex < this.totalPages - 1) this.pageIndex += 1;
  }

  setSort(col: CimDatatableColumn): void {
    if (this.sortDisabled) return;
    if (col.type === ColumnType.Input) return;
    if (this.sortKey === col.id) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = col.id;
      const numeric =
        col.type === ColumnType.Percentage ||
        col.type === ColumnType.Number2Decimal ||
        col.type === ColumnType.NumberMin1Decimal;
      this.sortDir = numeric ? 'desc' : 'asc';
    }
  }

  onCompanyInfoClick(company: any): void {
    if (!company?.Ticker) return;
    if (company.type === 'PORTFOLIO') {
      const id = company.Ticker.replace('PF_', '');
      const baseUrl = `${window.location.protocol}//${window.location.host}/#/`;
      window.open(`${baseUrl}app/portfolio-view/${id}/overview`, '_blank');
    } else {
      this.service.openCompanyInfoDialog(company.Ticker);
    }
  }

  onInputFocus(entity: any, prop: string): void {
    if (entity[prop] === 0) {
      entity[prop] = null;
    }
  }

  onInputChange(): void {
    this.inputChange.emit();
  }

  isString(value: any): boolean {
    return typeof value === 'string';
  }

  isNumericCol(col: CimDatatableColumn): boolean {
    return (
      col.type === ColumnType.Percentage ||
      col.type === ColumnType.Number2Decimal ||
      col.type === ColumnType.NumberMin1Decimal ||
      col.textRight === true
    );
  }

  isAssetCol(col: CimDatatableColumn): boolean {
    return col.type === ColumnType.CompanyInfo;
  }
}
